/**
 * "Abrechenbare Tenancies" — the candidate table.
 *
 * Rows come from the existing month PREVIEW endpoint (amounts, tenant, reference,
 * contact state). The per-row STATUS comes from two authoritative sources only:
 *   - the ledger (already created / failed / needs review), and
 *   - the backend PREFLIGHT (`ready`) for rows that have actually been checked.
 * The table never derives readiness itself.
 *
 * Clicking a row SELECTS it and nothing else. It starts no check, changes no status
 * and reaches no API — checking is an explicit act, either per row in the preview
 * panel or for the whole month from the workflow card.
 *
 * Responsive: a real table on md+, stacked cards below — no 3-column miniature and no
 * horizontally destroyed layout.
 */
import React from "react";

import BexioDraftStatusBadge from "./BexioDraftStatusBadge";
import { ROW_STATUS, formatChf, monthLabel } from "./bexioDraftLabels";

function ContactState({ status }) {
  const ok = status === "mapped";
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs ${
        ok ? "text-foreground" : "text-amber-700 dark:text-amber-400"
      }`}
    >
      <span aria-hidden="true">{ok ? "✓" : "⚠"}</span>
      {ok
        ? "Kontakt OK"
        : status === "ambiguous"
        ? "Kontakt mehrdeutig"
        : "Kontakt fehlt"}
    </span>
  );
}

function RowAction({
  row,
  onCreate,
  onCheckProvider,
  onReplace,
  canCreate,
  createBlockedReason,
  busy,
}) {
  // A claim whose Bexio document was proven gone. It is NOT armed for the normal
  // create button — that path would try to claim a fresh month and be refused. The
  // replacement is its own action with its own confirmation.
  if (row.status === ROW_STATUS.PROVIDER_MISSING) {
    return (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onReplace(row);
        }}
        disabled={!canCreate || busy}
        title={canCreate ? undefined : "Nur Administratoren können Entwürfe erstellen"}
        className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
      >
        Ersatzentwurf erstellen
      </button>
    );
  }

  if (row.documentNr) {
    // Created and still pointing at a Bexio document. The operator can ask whether
    // that document is really still there; the check itself writes nothing unless
    // Bexio answers with a definitive 404.
    return (
      <div className="flex items-center justify-end gap-2">
        <span className="text-sm font-medium tabular-nums text-foreground">
          {row.documentNr}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onCheckProvider(row);
          }}
          disabled={!canCreate || busy}
          title={
            canCreate
              ? "Prüft einmalig bei Bexio, ob dieser Beleg noch existiert"
              : "Nur Administratoren können Entwürfe prüfen"
          }
          className="rounded-lg border border-input bg-background px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
        >
          Bexio-Entwurf prüfen
        </button>
      </div>
    );
  }
  // READY is the ONLY status that arms this. Every other state — including a row
  // nobody has checked yet, and a row whose last create attempt failed — leaves it
  // disabled, so the fail-closed default survives any new status added later.
  const enabled = canCreate && row.status === ROW_STATUS.READY && !busy;
  const title = !canCreate
    ? createBlockedReason === "ledger"
      ? "Ledger-Daten unvollständig — Erstellen ist gesperrt"
      : "Nur Administratoren können Entwürfe erstellen"
    : row.status === ROW_STATUS.READY
    ? undefined
    : row.status === ROW_STATUS.NOT_CHECKED
    ? "Diesen Entwurf zuerst prüfen"
    : "Erst nach Klärung der Blocker möglich";

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onCreate(row);
      }}
      disabled={!enabled}
      title={title}
      className="rounded-lg border border-input bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
    >
      Entwurf erstellen
    </button>
  );
}

export default function BexioDraftTable({
  rows,
  year,
  month,
  selectedId,
  onSelect,
  onCreate,
  onCheckProvider,
  onReplace,
  canCreate,
  createBlockedReason,
  busyTenancyId,
  search,
  onSearchChange,
  loading,
}) {
  const period = monthLabel(year, month);

  return (
    <section className="rounded-xl border border-input bg-card shadow-sm">
      <div className="flex flex-col gap-3 border-b border-input p-4 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
          Abrechenbare Tenancies
          <span className="rounded-md bg-muted px-1.5 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
            {rows.length}
          </span>
        </h2>
        <div>
          <label htmlFor="bexio-draft-search" className="sr-only">
            Tenancies durchsuchen
          </label>
          <input
            id="bexio-draft-search"
            type="search"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Suche …"
            className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:w-64"
          />
        </div>
      </div>

      {loading && rows.length === 0 ? (
        <div className="space-y-2 p-4">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-10 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground">
          Für {period} sind keine abrechenbaren Mietverhältnisse vorhanden.
        </p>
      ) : (
        <>
          {/* ── md+ : table ── */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-input text-left">
                  <th scope="col" className="px-4 py-2.5 font-medium text-muted-foreground">Referenz</th>
                  <th scope="col" className="px-4 py-2.5 font-medium text-muted-foreground">Mieter / Kontakt</th>
                  <th scope="col" className="px-4 py-2.5 font-medium text-muted-foreground">Periode</th>
                  <th scope="col" className="px-4 py-2.5 text-right font-medium text-muted-foreground">Betrag</th>
                  <th scope="col" className="px-4 py-2.5 font-medium text-muted-foreground">Kontaktstatus</th>
                  <th scope="col" className="px-4 py-2.5 font-medium text-muted-foreground">Status</th>
                  <th scope="col" className="px-4 py-2.5 text-right font-medium text-muted-foreground">Aktion</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const selected = row.tenancyId === selectedId;
                  return (
                    <tr
                      key={row.tenancyId}
                      onClick={() => onSelect(row.tenancyId)}
                      aria-selected={selected}
                      className={`cursor-pointer border-b border-input last:border-0 ${
                        selected
                          ? "bg-blue-50 dark:bg-blue-500/10"
                          : "hover:bg-muted/60"
                      }`}
                    >
                      <td className="px-4 py-3 font-medium text-foreground">
                        {row.reference || "—"}
                      </td>
                      <td className="px-4 py-3 text-foreground">{row.tenantName || "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{period}</td>
                      <td className="px-4 py-3 text-right tabular-nums text-foreground">
                        {formatChf(row.amount)}
                      </td>
                      <td className="px-4 py-3">
                        <ContactState status={row.contactStatus} />
                      </td>
                      <td className="px-4 py-3">
                        <BexioDraftStatusBadge status={row.status} />
                      </td>
                      <td className="px-4 py-3 text-right">
                        <RowAction
                          row={row}
                          onCreate={onCreate}
                          onCheckProvider={onCheckProvider}
                          onReplace={onReplace}
                          canCreate={canCreate}
                          createBlockedReason={createBlockedReason}
                          busy={busyTenancyId === row.tenancyId}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* ── mobile : cards ── */}
          <ul className="divide-y divide-input md:hidden">
            {rows.map((row) => {
              const selected = row.tenancyId === selectedId;
              return (
                <li key={row.tenancyId}>
                  <button
                    type="button"
                    onClick={() => onSelect(row.tenancyId)}
                    className={`w-full px-4 py-3 text-left ${
                      selected ? "bg-blue-50 dark:bg-blue-500/10" : ""
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-foreground">
                          {row.tenantName || "—"}
                        </div>
                        <div className="truncate text-xs text-muted-foreground">
                          {row.reference || "—"} · {period}
                        </div>
                      </div>
                      <BexioDraftStatusBadge status={row.status} />
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <ContactState status={row.contactStatus} />
                      <span className="tabular-nums text-foreground">
                        {formatChf(row.amount)}
                      </span>
                    </div>
                    <div className="mt-2 flex justify-end">
                      <RowAction
                        row={row}
                        onCreate={onCreate}
                        onCheckProvider={onCheckProvider}
                        onReplace={onReplace}
                        canCreate={canCreate}
                        createBlockedReason={createBlockedReason}
                        busy={busyTenancyId === row.tenancyId}
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
